import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_COLUMNS, MAX_COLUMNS, MIN_COLUMNS, clampColumns, springEasing } from '@/lib/library/layout';

const STORAGE_KEY = 'library-columns';

/** Spread (or squeeze) the fingers this much past the gesture's start to take one step. */
const STEP_RATIO = 1.22;
/** Past the last step the grid still follows the fingers, at a fifth of the travel. */
const RUBBER = 0.2;
/** Clicks right after a pinch are the fingers lifting, not taps. */
const CLICK_GUARD_MS = 350;

export function readStoredColumns(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? clampColumns(Number(raw)) : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

export function storeColumns(columns: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(columns));
  } catch {
    // Private mode or blocked storage: the grid still works, it just forgets
  }
}

export interface Point {
  x: number;
  y: number;
}

/** What the grid needs to reflow from the live pinch to the new column count. */
export interface PinchCommit {
  from: number;
  to: number;
  /** The live scale on the grid layer when the step was taken. */
  scale: number;
  /** Transform origin of the live scale, in layer coordinates: the point that stays under the fingers. */
  origin: Point;
  /** Live translation (the fingers' midpoint drift), in px. */
  translate: Point;
  /** The fingers' midpoint now, relative to the scroll container's top-left. */
  focus: Point;
}

interface Options {
  scrollRef: React.RefObject<HTMLElement>;
  layerRef: React.RefObject<HTMLElement>;
  columns: number;
  /** Take a step. Must render the new column count synchronously (flushSync). */
  onCommit: (commit: PinchCommit) => void;
  reducedMotion: boolean;
  enabled?: boolean;
  /** The visible box the fingers are measured against; the scroller's rect by default (the window when the page scrolls). */
  getBox?: () => DOMRect;
}

interface Gesture {
  startDistance: number;
  startMid: Point;
  origin: Point;
  /** Re-read the baseline (distance, midpoint, origin) on the next move: set after each step. */
  stale: boolean;
  scale: number;
  translate: Point;
}

function distance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function midpoint(a: Touch, b: Touch): Point {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/**
 * Two-finger pinch (and trackpad pinch, ctrl + wheel) on the Library grid
 * steps between 2 and 5 columns. While the fingers move, the grid layer
 * scales live around their midpoint; crossing a step hands off to the grid
 * (onCommit), which reflows and FLIPs every tile. Letting go between steps
 * springs the layer back. One-finger scrolling is never touched.
 *
 * iOS Safari ignores user-scalable=no, so gesturestart / gesturechange are
 * cancelled inside the grid, and the container carries touch-action: pan-y.
 */
export function usePinchColumns({ scrollRef, layerRef, columns, onCommit, reducedMotion, enabled = true, getBox }: Options) {
  const getBoxRef = useRef(getBox);
  useEffect(() => {
    getBoxRef.current = getBox;
  });
  const [pinching, setPinching] = useState(false);
  const columnsRef = useRef(columns);
  const commitRef = useRef(onCommit);
  const reducedRef = useRef(reducedMotion);
  const gestureRef = useRef<Gesture | null>(null);
  const settleRef = useRef<Animation | null>(null);
  const lastEndRef = useRef(0);

  useEffect(() => {
    columnsRef.current = columns;
    commitRef.current = onCommit;
    reducedRef.current = reducedMotion;
  }, [columns, onCommit, reducedMotion]);

  const applyTransform = useCallback(
    (g: Gesture) => {
      const layer = layerRef.current;
      if (!layer) return;
      if (reducedRef.current || (g.scale === 1 && g.translate.x === 0 && g.translate.y === 0)) {
        layer.style.transform = '';
        return;
      }
      layer.style.transformOrigin = `${g.origin.x}px ${g.origin.y}px`;
      layer.style.transform = `translate3d(${g.translate.x}px, ${g.translate.y}px, 0) scale(${g.scale})`;
    },
    [layerRef],
  );

  const clearTransform = useCallback(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.style.transform = '';
    layer.style.willChange = '';
  }, [layerRef]);

  /** The baseline for a (re)started gesture: origin is the midpoint in untransformed layer coordinates. */
  const rebase = useCallback(
    (g: Gesture, mid: Point, dist: number) => {
      const layer = layerRef.current;
      if (!layer) return;
      const rect = layer.getBoundingClientRect(); // untransformed: transform is cleared at every rebase
      g.startDistance = dist;
      g.startMid = mid;
      g.origin = { x: mid.x - rect.left, y: mid.y - rect.top };
      g.stale = false;
      g.scale = 1;
      g.translate = { x: 0, y: 0 };
    },
    [layerRef],
  );

  const begin = useCallback(() => {
    settleRef.current?.cancel();
    settleRef.current = null;
    clearTransform();
    const layer = layerRef.current;
    if (layer && !reducedRef.current) layer.style.willChange = 'transform';
    gestureRef.current = { startDistance: 1, startMid: { x: 0, y: 0 }, origin: { x: 0, y: 0 }, stale: true, scale: 1, translate: { x: 0, y: 0 } };
    setPinching(true);
  }, [clearTransform, layerRef]);

  /** rawScale is the fingers' spread relative to the baseline. */
  const update = useCallback(
    (rawScale: number, mid: Point) => {
      const g = gestureRef.current;
      const scroller = scrollRef.current;
      if (!g || !scroller) return;
      const current = columnsRef.current;

      if (rawScale >= STEP_RATIO && current > MIN_COLUMNS) {
        commit(g, current, current - 1, mid);
        return;
      }
      if (rawScale <= 1 / STEP_RATIO && current < MAX_COLUMNS) {
        commit(g, current, current + 1, mid);
        return;
      }

      let scale = rawScale;
      if (rawScale > 1 && current <= MIN_COLUMNS) scale = 1 + (rawScale - 1) * RUBBER;
      if (rawScale < 1 && current >= MAX_COLUMNS) scale = 1 - (1 - rawScale) * RUBBER;
      g.scale = Math.min(1.6, Math.max(0.6, scale));
      g.translate = { x: mid.x - g.startMid.x, y: mid.y - g.startMid.y };
      applyTransform(g);

      function commit(gesture: Gesture, from: number, to: number, focusClient: Point) {
        const box = getBoxRef.current?.() ?? scroller!.getBoundingClientRect();
        const info: PinchCommit = {
          from,
          to,
          scale: reducedRef.current ? 1 : gesture.scale,
          origin: gesture.origin,
          translate: reducedRef.current ? { x: 0, y: 0 } : gesture.translate,
          focus: { x: focusClient.x - box.left, y: focusClient.y - box.top },
        };
        clearTransform();
        columnsRef.current = to;
        gesture.stale = true;
        commitRef.current(info);
        const layer = layerRef.current;
        if (layer && !reducedRef.current) layer.style.willChange = 'transform';
      }
    },
    [applyTransform, clearTransform, layerRef, scrollRef],
  );

  const end = useCallback(() => {
    const g = gestureRef.current;
    gestureRef.current = null;
    lastEndRef.current = performance.now();
    setPinching(false);
    const layer = layerRef.current;
    if (!g || !layer) return;
    const from = layer.style.transform;
    clearTransform();
    if (!from || reducedRef.current || typeof layer.animate !== 'function') return;
    const spring = springEasing(500, 38);
    layer.style.transformOrigin = `${g.origin.x}px ${g.origin.y}px`;
    settleRef.current = layer.animate([{ transform: from }, { transform: 'none' }], { duration: spring.duration, easing: supportsLinearEasing() ? spring.easing : 'cubic-bezier(.22, 1, .36, 1)' });
    settleRef.current.onfinish = () => {
      settleRef.current = null;
    };
  }, [clearTransform, layerRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        begin();
        e.preventDefault();
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (!g || e.touches.length < 2) return;
      if (e.cancelable) e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.max(1, distance(a, b));
      const mid = midpoint(a, b);
      if (g.stale) rebase(g, mid, dist);
      update(dist / g.startDistance, mid);
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (gestureRef.current && e.touches.length < 2) end();
    };

    // Trackpad pinch arrives as ctrl + wheel
    let wheelScale = 1;
    let wheelTimer = 0;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (!gestureRef.current) {
        begin();
        wheelScale = 1;
      }
      const g = gestureRef.current!;
      const mid = { x: e.clientX, y: e.clientY };
      if (g.stale) {
        rebase(g, mid, 1);
        wheelScale = 1;
      }
      wheelScale *= Math.exp(-e.deltaY * 0.01);
      g.startMid = mid; // a pointer pinch has no drift
      update(wheelScale, mid);
      window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(end, 160);
    };

    const blockGesture = (e: Event) => e.preventDefault();
    const guardClick = (e: MouseEvent) => {
      if (gestureRef.current || performance.now() - lastEndRef.current < CLICK_GUARD_MS) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', blockGesture, { passive: false } as AddEventListenerOptions);
    el.addEventListener('gesturechange', blockGesture, { passive: false } as AddEventListenerOptions);
    el.addEventListener('click', guardClick, true);
    return () => {
      window.clearTimeout(wheelTimer);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', blockGesture);
      el.removeEventListener('gesturechange', blockGesture);
      el.removeEventListener('click', guardClick, true);
    };
  }, [begin, enabled, end, rebase, scrollRef, update]);

  return { pinching };
}

let linearSupport: boolean | null = null;
/** CSS linear() easing (Safari 17.2+, Chrome 113+). Older engines get the brand ease-out. */
export function supportsLinearEasing(): boolean {
  if (linearSupport === null) {
    try {
      linearSupport = typeof CSS !== 'undefined' && CSS.supports('transition-timing-function', 'linear(0, 1)');
    } catch {
      linearSupport = false;
    }
  }
  return linearSupport;
}
